/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Form } from 'react-bootstrap'
import DatePickerComponent from '@mdsol/lego/lib/DatePicker'
import Modal from '../Modal'
import { ErrorMessageStyled } from '../shared/ErrorMessage'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import { useEsignature } from '../../hooks/useEsignature'
import { getMomentDateFormat, getDatePickerParseFormats } from '~/utils/dateFormatting'

const DatePicker = DatePickerComponent as any
/**
 * Set Expiration Date Modal Component
 *
 * Allows users to set or clear expiration dates for selected files.
 * Supports batch operations for:
 * - Multiple files (standard)
 * - Property search results (batch)
 * - Folder-wide changes (batch)
 *
 * Features:
 * - Date selection with DatePicker
 * - Conditional required validation (if any file requires expiration date)
 * - E-signature validation support
 * - Integration with file update events
 * - Cache invalidation for updated files
 *
 * Migrated from: lego/page/browser/SetExpirationDateModal.jsx
 */

interface File {
  id: number
  requireExpirationDate?: boolean
}

interface SetExpirationDateModalProps {
  options: {
    selectedIds: number[]
    selectedWholeResult?: boolean
    selectedItems?: File[]
    parentFolderId?: number
    isPropertiesSearch?: boolean
    filter?: any
    meta?: any
    [key: string]: unknown
  }
  onClose?: () => void
}

interface FormValues {
  expiresAt: any
}

export const SetExpirationDateModal: React.FC<SetExpirationDateModalProps> = ({ options, onClose }) => {
  const { t: translate } = useTranslation()
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { api, $rootScope } = context
  const { signAction } = useEsignature()
  const { hideModal } = useModal()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [requireExpirationDate, setRequireExpirationDate] = useState(false)
  const defaultFilterConst: FormValues = { expiresAt: null }

  useEffect(() => {
    setIsOpen(true)
    checkIfExpirationDateRequired()
  }, [])

  const doClose = () => {
    setIsOpen(false)
    hideModal()
    if (onClose) {
      onClose()
    }
  }

  const checkIfExpirationDateRequired = () => {
    // If selecting all results, require expiration date
    if (options?.selectedWholeResult) {
      setRequireExpirationDate(true)
      return
    }

    // Check if any selected file requires expiration date
    const anyRequireExpiration = options?.selectedItems?.some((item) => item?.requireExpirationDate) || false
    setRequireExpirationDate(anyRequireExpiration)
  }

  const save = async (values: FormValues) => {
    // Close modal immediately (don't wait for API)
    doClose()

    // Convert date to Unix timestamp at midnight
    let expiresAtTimestamp: number | null = null
    if (values.expiresAt) {
      const date = new Date(values.expiresAt)
      date.setHours(0, 0, 0, 0)
      expiresAtTimestamp = date.getTime()
    }

    const doSetExpirationDateCallback = async (validation?: Record<string, unknown>) => {
      try {
        await api.files.setExpirationDateFiles(options.selectedIds, expiresAtTimestamp, validation, options)

        // Emit global events for backward compatibility (window event bus)
        window.dispatchEvent(new CustomEvent('updateFiles', { detail: true }))
        window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateDashboard', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateSearchFiles'))

        // Invalidate React Query caches
        queryClient.invalidateQueries({ queryKey: ['files'] })
        queryClient.invalidateQueries({ queryKey: ['documents'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })

        toast.success(translate('BROWSER_SETTINGS_SET_EXPIRATION_DATE_SUCCESS_MSG'))
      } catch (error: any) {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        toast.error(translate('ERROR_ERROR', { error: errorMessage }))
      }
    }

    // Check if e-signature is required
    if (auth.requireESignatureForFileCommands()) {
      try {
        const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_SET_FSI_EXPIRATION_DATE')
        await doSetExpirationDateCallback(validation as Record<string, unknown>)
      } catch (error) {
        // E-signature cancelled or failed - do nothing
        console.log('E-signature cancelled')
      }
    } else {
      await doSetExpirationDateCallback()
    }
  }

  // Create validation schema dynamically based on requireExpirationDate
  const validationSchema = Yup.object({
    expiresAt: requireExpirationDate
      ? Yup.date().nullable().required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
      : Yup.date().nullable()
  })

  return (
    <Formik<FormValues>
      validate={(values) => {
        const errors: Partial<Record<keyof FormValues, string>> = {}
        if (requireExpirationDate && !values.expiresAt) {
          errors.expiresAt = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
        }
        return errors
      }}
      enableReinitialize={true}
      initialValues={defaultFilterConst}
      onSubmit={save}
    >
      {({ values, setFieldValue, handleSubmit, errors, touched }) => (
        <Modal
          show={isOpen}
          handleClose={doClose}
          title={translate('BROWSER_SETTINGS_SET_EXPIRATION_DATE_H_TITLE')}
          className="theme-sandman"
          style={{ overflow: 'visible' }}
          size="md"
        >
          <Modal.Body>
            <div>
              <div className="form-group w50p">
                <label className="control-label" htmlFor="expiration-date-picker">
                  {translate('BROWSER_SETTINGS_SET_EXPIRATION_DATE_LABEL')}
                </label>
                <DatePicker
                  id="expiration-date-picker"
                  name="expiresAt"
                  selected={values.expiresAt}
                  onChange={(val: any) => {
                    setFieldValue('expiresAt', val)
                  }}
                  popoverTargetOffset="3px 0"
                  todayButton="Today"
                  dateFormatCalendar="MMMM"
                  dateFormat={getDatePickerParseFormats()}
                  showYearDropdown
                  className="form-control field-control w100p"
                />
                {requireExpirationDate && errors.expiresAt && touched.expiresAt && (
                  <ErrorMessageStyled name="expiresAt" />
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="text-right">
              <Button className="btn btn-chita btn-close" data-action="cancel" onClick={doClose}>
                {translate('CANCEL')}
              </Button>
              <Button data-action="confirm" className="btn btn-primary text-capitalize" onClick={() => handleSubmit()}>
                {translate('OK')}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}

export default SetExpirationDateModal
